import { getTokenHeader } from "@/helpers/commonHelper";
import axios from "axios";
import moment from "moment";
const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);

import "./NewsAppStatistics.scss";
import LineChart from "@/components/Common/LineChart/LineChart";
import StackedBarChart from "@/components/Common/StackedBarChart/StackedBarChart";
import DateInput from "@/components/Common/DateInput/DateInput.vue";

const defaultChartConfig = {
  config: {},
  sumVisitsData: {
    labels: [],
    datasets: [
      {
        backgroundColor: [],
        data: []
      }
    ]
  },
  options: {
    legend: {
      display: true
    },
    responsive: true,
    maintainAspectRatio: false,
    chartStyle: { height: "300px", position: "relative" }
  }
};

const today = new Date();
today.setHours(0, 0, 0);
const lastMonth = new Date();
lastMonth.setHours(23, 59, 59);
lastMonth.setDate(today.getDate() - 30);

export default {
  name: "NewsAppStatistics",
  components: { DateInput, LineChart, StackedBarChart },
  props: {
    expanded: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    sumAppDownloadsChartConfig: { ...defaultChartConfig },
    sumAppRequestsChartConfig: { ...defaultChartConfig },
    dailyAppDownloadsChartConfig: { ...defaultChartConfig },
    currentAppStatistics: null,
    allAppStatistics: null,
    fromDate: lastMonth,
    toDate: today
  }),
  async mounted() {
    this.currentAppStatistics = null;
    this.allAppStatistics = null;
    this.fromDate = lastMonth;
    this.toDate = today;
    if (this.principalHasAppConfigured) {
      await this.getAppStatistics();
    }
  },
  computed: {
    currentPrincipal() {
      return this.$store.state.selectedPrincipal;
    },
    principalHasAppConfigured() {
      const { apiId, apiKey } =
        this.currentPrincipal?.additional_data?.appdinxLogin ?? {};
      return apiId?.length && apiKey?.length;
    },
    appStatisticsCreationDateFormatted() {
      if (
        !this.principalHasAppConfigured ||
        !this.currentAppStatistics?.creationDate
      ) {
        return;
      }
      const date = moment(this.currentAppStatistics.creationDate).format(
        "DD.MM.YYYY | HH:mm"
      );
      return `${date} Uhr`;
    },
    cAllAppStatistics() {
      const start = new Date(this.fromDate);
      start.setHours(0, 0, 0);
      const end = new Date(this.toDate);
      end.setHours(23, 59, 59);
      return this.allAppStatistics.filter(statistics => {
        const statisticsDate = new Date(statistics.create_date);
        return statisticsDate >= start && statisticsDate <= end;
      });
    },
    fromDateFormatted() {
      return this.fromDate
        ? this.fromDate.toLocaleDateString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
          })
        : "";
    },
    toDateFormatted() {
      return this.toDate
        ? this.toDate.toLocaleDateString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
          })
        : "";
    }
  },
  methods: {
    async getAppStatistics() {
      const headers = getTokenHeader();
      const params = {
        principal_id: this.currentPrincipal?.id,
        expanded: Number(this.expanded)
      };
      try {
        const res = await axios.get(
          `${env.news_api_client_url}news/statistics/app`,
          {
            headers,
            params
          }
        );
        this.allAppStatistics = res.data;
        this.currentAppStatistics = this.allAppStatistics.find(
          statistics => statistics.isCurrent
        ).statistics;

        if (this.expanded) {
          this.$nextTick(() => {
            this.updateChartData();
          });
        }
      } catch (e) {
        console.error(e);
      }
    },
    updateChartData() {
      try {
        const labels = this.cAllAppStatistics.map(obj => {
          return new Date(obj.create_date).toLocaleDateString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
          });
        });

        const androidInstallCount = this.cAllAppStatistics.map(
          obj => obj.statistics.androidCount
        );
        const iosInstallCount = this.cAllAppStatistics.map(
          obj => obj.statistics.iosCount
        );

        this.sumAppDownloadsChartConfig.sumVisitsData = {
          labels: labels,
          datasets: [
            {
              label: "Downloads Android",
              backgroundColor: "#00636C",
              borderColor: "#00636C",
              borderWidth: 2,
              data: androidInstallCount,
              fill: false
            },
            {
              label: "Downloads IOS",
              backgroundColor: "#1571ce",
              borderColor: "#1571ce",
              borderWidth: 2,
              data: iosInstallCount,
              fill: false
            }
          ]
        };

        const totalInstallationsOfTheDayCount = this.cAllAppStatistics.map(
          obj => obj.statistics.installationsOfTheDayCount?.total
        );
        const androidInstallationsOfTheDayCount = this.cAllAppStatistics.map(
          obj => obj.statistics.installationsOfTheDayCount?.android
        );
        const iosInstallationsOfTheDayCount = this.cAllAppStatistics.map(
          obj => obj.statistics.installationsOfTheDayCount?.ios
        );
        this.dailyAppDownloadsChartConfig.sumVisitsData = {
          labels: labels,
          datasets: [
            {
              label: "Downloads insgesamt",
              backgroundColor: "#000",
              borderColor: "#000",
              borderWidth: 2,
              data: totalInstallationsOfTheDayCount,
              total: totalInstallationsOfTheDayCount
                .filter(x => !!x)
                .reduce((a, b) => a + b, 0),
              fill: false
            },
            {
              label: "Downloads Android",
              backgroundColor: "#00636C",
              borderColor: "#00636C",
              borderWidth: 2,
              data: androidInstallationsOfTheDayCount,
              total: androidInstallationsOfTheDayCount
                .filter(x => !!x)
                .reduce((a, b) => a + b, 0),
              fill: false,
              hidden: true
            },
            {
              label: "Downloads IOS",
              backgroundColor: "#1571ce",
              borderColor: "#1571ce",
              borderWidth: 2,
              data: iosInstallationsOfTheDayCount,
              total: iosInstallationsOfTheDayCount
                .filter(x => !!x)
                .reduce((a, b) => a + b, 0),
              fill: false,
              hidden: true
            }
          ]
        };

        const totalRequestsOfTheDayCount = this.cAllAppStatistics.map(
          obj => obj.statistics.requestsOfTheDayCount
        );

        const totalFingerprintsOfTheDayCount = this.cAllAppStatistics.map(
          obj => obj.statistics.fingerprintsOfTheDayCount
        );

        this.sumAppRequestsChartConfig.sumVisitsData = {
          labels: labels,
          datasets: [
            {
              label: "Aufrufe",
              backgroundColor: "#00636C",
              borderColor: "#00636C",
              borderWidth: 2,
              data: totalRequestsOfTheDayCount,
              total: totalRequestsOfTheDayCount.reduce((a, b) => a + b, 0),
              fill: false
            },
            {
              label: "Besucher",
              backgroundColor: "#1571ce",
              borderColor: "#1571ce",
              borderWidth: 2,
              data: totalFingerprintsOfTheDayCount,
              total: totalFingerprintsOfTheDayCount.reduce((a, b) => a + b, 0),
              fill: false
            }
          ]
        };

        Object.values(this.$refs).forEach(chart => chart.render());
      } catch (e) {
        console.error(e);
      }
    }
  },
  watch: {
    fromDate(newValue, oldValue) {
      const newTime = newValue?.getTime();
      const oldTime = oldValue?.getTime();
      if (!newTime) {
        this.fromDate = oldValue;
        return;
      }
      if (newTime === oldTime) {
        return;
      }
      this.updateChartData();
    },
    toDate(newValue, oldValue) {
      const newTime = newValue?.getTime();
      const oldTime = oldValue?.getTime();
      if (!newTime) {
        this.toDate = oldValue;
        return;
      }
      if (newTime === oldTime) {
        return;
      }
      this.updateChartData();
    }
  }
};
